import React from 'react';
import { Form, Select, DatePicker, Checkbox, Row, Col, Tag } from 'antd';
import { CalendarIcon } from 'components/common/icons';
import { FilterDrawer, SupplierSelect } from 'components/common';
import { ClientSelect } from 'components/common/clientSelect';
import { inboundStatuses, outboundStatuses } from 'constants/warehouse';
import { useFilterPanel } from './useFilterPanel';
import type { InboundFilters, OutboundFilters } from 'models/Warehouse';

const { RangePicker } = DatePicker;

// Status color mapping
const statusColors: Record<string, { background: string; color: string }> = {
  // Inbound
  SHIPPED: { background: '#e3e8ff', color: '#5b6ef5' },
  IN_WAREHOUSE: { background: '#d4f4e8', color: '#05796b' },
  CLAIMED: { background: '#ffe5e9', color: '#e91e63' },
  PARTLY_CLAIMED: { background: '#ffe5f0', color: '#e91e63' },
  COMPLETED: { background: '#e8f5e9', color: '#4caf50' },
  // Outbound
  READY_TO_DELIVERY: { background: '#d4f4e8', color: '#05796b' },
  IN_PROGRESS: { background: '#ffe8d6', color: '#f57c00' },
  PENDING_LABEL: { background: '#fff0e0', color: '#f57c00' },
  LABEL_GENERATED: { background: '#e3f2fd', color: '#2196f3' },
};

interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  onApplyInbound?: (filters: InboundFilters) => void;
  onApplyOutbound?: (filters: OutboundFilters) => void;
  type: 'inbound' | 'outbound';
  initialInboundFilters?: InboundFilters;
  initialOutboundFilters?: OutboundFilters;
  suppliers?: { id: string; name: string }[];
  loading?: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  open,
  onClose,
  onApplyInbound,
  onApplyOutbound,
  type,
  initialInboundFilters = {},
  initialOutboundFilters = {},
}) => {
  const { form, isInbound, handleApply, handleClear } = useFilterPanel({
    open,
    type,
    initialInboundFilters,
    initialOutboundFilters,
    onApplyInbound,
    onApplyOutbound,
  });

  return (
    <FilterDrawer
      title="Filter"
      placement="left"
      width={400}
      styles={{
        body: {
          marginBottom: 80,
        },
      }}
      onClose={onClose}
      open={open}
      onApply={handleApply}
      onClear={handleClear}
      onExpand={onClose}
    >
      <Form form={form} layout="vertical" onFinish={handleApply}>
        <Row gutter={[0, 16]}>
          {isInbound ? (
            <Col span={24}>
              <Form.Item label="Supplier" name="supplier">
                <SupplierSelect placeholder={'Select Supplier'} />
              </Form.Item>
            </Col>
          ) : (
            <Col span={24}>
              <Form.Item label="Client" name="client">
                <ClientSelect placeholder="Select client" />
              </Form.Item>
            </Col>
          )}
          {isInbound ? (
            <>
              <Col span={24}>
                <Form.Item label="Expected Arrival Date" name="expectedArrivalDate">
                  <RangePicker suffixIcon={<CalendarIcon />} className="w-100" popupClassName="fmg-range-picker" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="Shipment Date" name="shipmentDate">
                  <RangePicker suffixIcon={<CalendarIcon />} className="w-100" popupClassName="fmg-range-picker" />
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col span={24}>
              <Form.Item label="Expected Delivery Date" name="expectedDeliveryDate">
                <RangePicker suffixIcon={<CalendarIcon />} className="w-100" popupClassName="fmg-range-picker" />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.Item label="Status" name="status">
              <Select
                mode="multiple"
                placeholder="Select status"
                options={isInbound ? inboundStatuses : outboundStatuses}
                allowClear
                tagRender={(props) => {
                  const { label, value, closable, onClose } = props;
                  const colors = statusColors[value as string] || { background: '#f5f5f5', color: '#8c8c8c' };
                  return (
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      style={{
                        backgroundColor: colors.background,
                        color: colors.color,
                        border: 'none',
                        borderRadius: '20px',
                        padding: '2px 8px',
                        margin: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        marginRight: '4px',
                      }}
                    >
                      {label}
                    </Tag>
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="showCompleted" valuePropName="checked">
              <Checkbox>Show Completed Orders</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </FilterDrawer>
  );
};
